import { useNavigate } from 'react-router-dom'
import { PageFull } from '@/presentation/components/PageFull'

export default function TermsAndConditions() {
  const navigate = useNavigate()

  const handleClose = () => {
    navigate('/login')
  }

  return (
    <PageFull className='relative'>
      <button
        onClick={handleClose}
        className='sticky right-6 top-6 z-50 ml-auto mr-6 mt-6 flex h-10 w-10 items-center justify-center rounded-full bg-white shadow-lg hover:shadow-xl transition-all hover:scale-110'
        data-testid='close-button'
        aria-label='Close'
      >
        <svg
          width='20'
          height='20'
          viewBox='0 0 16 16'
          fill='none'
          xmlns='http://www.w3.org/2000/svg'
        >
          <path
            d='M12 4L4 12M4 4l8 8'
            stroke='#5c17f2'
            strokeWidth='2'
            strokeLinecap='round'
            strokeLinejoin='round'
          />
        </svg>
      </button>

      <div className='px-6 pb-8' data-testid='terms-and-conditions-page'>
        <h1 className='mb-6 text-2xl font-bold text-[#5c17f2]'>
          Terms and Conditions
        </h1>

        <div className='space-y-6 text-sm leading-relaxed text-gray-700'>
          <section>
            <p
              className='mb-4 text-xs text-gray-500'
              data-testid='last-updated'
            >
              Last Updated: January 4, 2026
            </p>
            <p>
              Welcome to our Affiliate Partner Program. These Terms and
              Conditions govern your participation in our affiliate marketing
              program. By registering as an affiliate partner, you agree to
              comply with these terms.
            </p>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-definitions'
            >
              1. Definitions
            </h2>
            <p className='mb-2'>
              <strong>Affiliate/Partner:</strong> An individual or entity
              registered in our program to promote our gaming platform services.
            </p>
            <p className='mb-2'>
              <strong>Commission:</strong> The compensation paid to affiliates
              for qualified referrals and conversions.
            </p>
            <p className='mb-2'>
              <strong>Qualified Referral:</strong> A user who registers through
              your unique referral link and meets our validation criteria.
            </p>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-enrollment'
            >
              2. Enrollment and Eligibility
            </h2>
            <p className='mb-2'>
              To participate in our Affiliate Program, you must:
            </p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>Be at least 18 years of age</li>
              <li>Provide accurate registration information</li>
              <li>Have a valid payment account for commission withdrawal</li>
              <li>
                Comply with all applicable laws and regulations in your
                jurisdiction
              </li>
            </ul>
            <p className='mt-3'>
              We reserve the right to reject or revoke affiliate status at our
              sole discretion.
            </p>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-tracking'
            >
              3. Tracking and Measurement
            </h2>
            <p className='mb-2'>
              Referrals are tracked through unique affiliate links and device
              identifiers. We use:
            </p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>Persistent tracking tokens with 30-day attribution window</li>
              <li>Last-click attribution model</li>
              <li>Fraud detection systems to validate referrals</li>
            </ul>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-commission'
            >
              4. Commission Structure
            </h2>
            <p className='mb-2'>
              Commission rates are based on qualified user activities:
            </p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>Commission rates vary by user activity and retention</li>
              <li>Specific rates are displayed in your partner dashboard</li>
              <li>
                Commission tiers may be adjusted based on performance metrics
              </li>
            </ul>
            <p className='mt-3'>
              We reserve the right to modify commission structures with 30 days'
              advance notice.
            </p>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-payment'
            >
              5. Payment Terms
            </h2>
            <p className='mb-2'>
              <strong>Payment Schedule:</strong> Commissions are calculated
              monthly and paid according to Net-30 terms (30 days after the end
              of each month).
            </p>
            <p className='mb-2'>
              <strong>Minimum Payout:</strong> Minimum threshold of $50 USD for
              withdrawals.
            </p>
            <p className='mb-2'>
              <strong>Payment Methods:</strong> Payments are processed through
              supported withdrawal methods in your dashboard.
            </p>
            <p className='mt-3'>
              Commissions from fraudulent or reversed transactions will be
              deducted from future payments.
            </p>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-prohibited'
            >
              6. Prohibited Activities
            </h2>
            <p className='mb-2'>Affiliates must NOT:</p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>
                Engage in fraudulent activities, including fake registrations or
                self-referrals
              </li>
              <li>Use spam, unsolicited emails, or deceptive marketing</li>
              <li>
                Bid on our brand name or trademarked terms in paid search
                advertising
              </li>
              <li>Make false or misleading claims about our services</li>
              <li>
                Promote illegal activities or violate gambling regulations
              </li>
              <li>
                Use automated bots or scripts to generate artificial traffic
              </li>
            </ul>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-compliance'
            >
              7. Compliance and Disclosure
            </h2>
            <p className='mb-2'>
              Affiliates must comply with all applicable advertising laws,
              including:
            </p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>
                FTC Endorsement Guides (16 CFR Part 255) requiring clear
                disclosure of affiliate relationships
              </li>
              <li>
                GDPR, CCPA, and other data privacy regulations in your
                jurisdiction
              </li>
              <li>Age restrictions and responsible gaming requirements</li>
            </ul>
            <p className='mt-3'>
              You must clearly disclose your affiliate relationship when
              promoting our services.
            </p>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-ip'
            >
              8. Intellectual Property
            </h2>
            <p className='mb-2'>
              We grant you a limited, non-exclusive, revocable license to use
              our trademarks and promotional materials solely for program
              participation.
            </p>
            <p className='mt-3'>You may NOT:</p>
            <ul className='ml-6 list-disc space-y-1'>
              <li>Modify our logos or brand materials</li>
              <li>Register domain names containing our trademarks</li>
              <li>
                Create content that could damage our reputation or brand image
              </li>
            </ul>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-confidentiality'
            >
              9. Confidentiality
            </h2>
            <p>
              You agree to maintain confidentiality of all proprietary
              information, including commission rates, tracking technology, and
              business strategies disclosed through the program. This obligation
              survives termination of the agreement.
            </p>
          </section>

          <section>
            <h2
              className='mb-3 text-lg font-semibold text-[#5c17f2]'
              data-testid='section-termination'
            >
              10. Term and Termination
            </h2>
            <p className='mb-2'>
              <strong>Duration:</strong> This agreement continues until
              terminated by either party.
            </p>
            <p className='mb-2'>
              <strong>Termination Rights:</strong> Either party may terminate
              with 30 days' notice. We may terminate immediately for violations
              of these terms.
            </p>
            <p className='mt-3'>
              Upon termination, you must cease all promotional activities and
              remove our marketing materials. Outstanding commissions for
              validated referrals will be paid per normal schedule.
            </p>
          </section>

          <section>
            <h2 className='mb-3 text-lg font-semibold text-[#5c17f2]'>
              11. Indemnification
            </h2>
            <p>
              You agree to indemnify and hold harmless our company, its
              officers, directors, and employees from any claims, damages, or
              expenses arising from your promotional activities, including
              violations of these terms or applicable laws.
            </p>
          </section>

          <section>
            <h2 className='mb-3 text-lg font-semibold text-[#5c17f2]'>
              12. Limitation of Liability
            </h2>
            <p>
              Our liability for any claim related to the affiliate program is
              limited to the total commissions paid to you in the 12 months
              preceding the claim. We are not liable for indirect, incidental,
              or consequential damages.
            </p>
          </section>

          <section>
            <h2 className='mb-3 text-lg font-semibold text-[#5c17f2]'>
              13. Dispute Resolution
            </h2>
            <p className='mb-2'>
              Disputes shall be resolved through the following process:
            </p>
            <ol className='ml-6 list-decimal space-y-1'>
              <li>Submit written notice of dispute to our support team</li>
              <li>Attempt good-faith negotiation for 30 days</li>
              <li>
                If unresolved, proceed to binding arbitration under applicable
                arbitration rules
              </li>
            </ol>
          </section>

          <section>
            <h2 className='mb-3 text-lg font-semibold text-[#5c17f2]'>
              14. Modifications
            </h2>
            <p>
              We may update these Terms and Conditions at any time. Material
              changes will be communicated with 30 days' advance notice.
              Continued participation after changes take effect constitutes
              acceptance of modified terms.
            </p>
          </section>

          <section>
            <h2 className='mb-3 text-lg font-semibold text-[#5c17f2]'>
              15. Governing Law
            </h2>
            <p>
              These terms are governed by applicable laws in our jurisdiction.
              Any legal proceedings shall be conducted in the appropriate courts
              of our registered location.
            </p>
          </section>

          <section>
            <h2 className='mb-3 text-lg font-semibold text-[#5c17f2]'>
              16. Contact Information
            </h2>
            <p>
              For questions regarding these Terms and Conditions, please contact
              our affiliate support team through your partner dashboard.
            </p>
          </section>
        </div>
      </div>
    </PageFull>
  )
}
